import { signal } from '../state';
import { createRoot, runWithOwner } from '../state/scope';
export function createControlFlowRuntime(options) {
    const forChildCaches = new Map();
    const showBranchCaches = new Map();
    const switchBranchCaches = new Map();
    const disposeForChildCacheEntry = (entry) => {
        entry.dispose();
    };
    const disposeForChildCache = (nodeId) => {
        const cache = forChildCaches.get(nodeId);
        if (!cache) {
            return;
        }
        for (const entry of cache.values()) {
            disposeForChildCacheEntry(entry);
        }
        forChildCaches.delete(nodeId);
    };
    const disposeControlFlowBranchCaches = (nodeId) => {
        showBranchCaches.delete(nodeId);
        switchBranchCaches.delete(nodeId);
    };
    const renderForChildNodes = (node, item, indexAccessor, keyBase) => {
        const children = node.props?.children;
        const output = children(item, indexAccessor);
        if (Array.isArray(output)) {
            return output.map((child, childIndex) => {
                const normalized = options.ensureNodeId(child);
                if (!normalized.key && keyBase !== undefined) {
                    normalized.key = `${keyBase}:${childIndex}`;
                }
                return normalized;
            });
        }
        const normalized = options.ensureNodeId(output);
        if (!normalized.key && keyBase !== undefined) {
            normalized.key = keyBase;
        }
        return [normalized];
    };
    const renderForChildEntry = (node, item, itemSnapshot, initialIndex, keyBase) => {
        const indexSignal = signal(initialIndex);
        let renderedChildren = [];
        const register = () => createRoot((dispose) => {
            renderedChildren = renderForChildNodes(node, item, () => indexSignal(), keyBase);
            return dispose;
        });
        const owner = options.getEmitterOwner();
        const dispose = owner
            ? runWithOwner(owner, register)
            : register();
        return {
            item,
            itemSnapshot,
            index: indexSignal,
            children: renderedChildren,
            dispose,
        };
    };
    const resolveForChildrenWithCache = (node, keyValidation) => {
        const props = node.props ?? {};
        const each = props.each;
        const fallback = props.fallback;
        const keyFn = props.key;
        const resolved = typeof each === 'function'
            ? each()
            : each;
        const items = Array.isArray(resolved) ? resolved : [];
        if (!keyFn) {
            disposeForChildCache(node.id);
            return resolveForChildren(node, keyValidation, options.ensureNodeId);
        }
        if (items.length === 0) {
            disposeForChildCache(node.id);
            return normalizeOptionalChildren(fallback, options.ensureNodeId);
        }
        const cache = forChildCaches.get(node.id) ?? new Map();
        forChildCaches.set(node.id, cache);
        const nextKeys = new Set();
        const rawKeyCounts = new Map();
        const result = [];
        for (let i = 0; i < items.length; i += 1) {
            const item = items[i];
            if (item === undefined || item === null) {
                continue;
            }
            const index = i;
            const itemSnapshot = createItemSnapshot(item);
            const keyBase = resolveDuplicateAwareKey(node, keyFn(item, () => index), rawKeyCounts, keyValidation);
            nextKeys.add(keyBase);
            const cached = cache.get(keyBase);
            if (cached && Object.is(cached.item, item) && snapshotsEqual(cached.itemSnapshot, itemSnapshot)) {
                cached.index.set(index);
                result.push(...cached.children);
                continue;
            }
            if (cached) {
                disposeForChildCacheEntry(cached);
                cache.delete(keyBase);
            }
            const entry = renderForChildEntry(node, item, itemSnapshot, index, keyBase);
            cache.set(keyBase, entry);
            result.push(...entry.children);
        }
        for (const [cacheKey, entry] of cache.entries()) {
            if (!nextKeys.has(cacheKey)) {
                disposeForChildCacheEntry(entry);
                cache.delete(cacheKey);
            }
        }
        warnOnMissingForKeys(node, result, keyValidation);
        return result;
    };
    const trackSwitchNode = (node) => {
        const children = node.children ?? [];
        for (const child of children) {
            const matchNode = options.ensureNodeId(child);
            if (matchNode.type !== 'Match') {
                continue;
            }
            const when = matchNode.props?.when;
            if (typeof when === 'function') {
                when();
            }
        }
    };
    return {
        resolveChildren(node, keyValidation) {
            switch (node.type) {
                case 'Show':
                    return resolveShowChildrenCached(node, options.ensureNodeId, showBranchCaches);
                case 'For':
                    return resolveForChildrenWithCache(node, keyValidation);
                case 'SwitchFlow':
                    return resolveSwitchChildrenCached(node, options.ensureNodeId, switchBranchCaches);
                default:
                    return node.children ?? [];
            }
        },
        trackNode(node) {
            switch (node.type) {
                case 'Show':
                    trackShowNode(node);
                    return;
                case 'For':
                    trackForNode(node);
                    return;
                case 'SwitchFlow':
                    trackSwitchNode(node);
                    return;
                default:
                    return;
            }
        },
        describeSelection(node) {
            switch (node.type) {
                case 'Show':
                    return getShowValue(node);
                case 'For':
                    return getForSelection(node);
                case 'SwitchFlow':
                    return getSwitchBranch(node, options.ensureNodeId);
                default:
                    return null;
            }
        },
        disposeNode(nodeId) {
            disposeForChildCache(nodeId);
            disposeControlFlowBranchCaches(nodeId);
        },
        disposeAll() {
            for (const cache of forChildCaches.values()) {
                for (const entry of cache.values()) {
                    disposeForChildCacheEntry(entry);
                }
            }
            forChildCaches.clear();
            showBranchCaches.clear();
            switchBranchCaches.clear();
        },
    };
}
function trackShowNode(node) {
    const props = node.props ?? {};
    const when = props.when;
    if (typeof when === 'function') {
        when();
    }
}
function getShowValue(node) {
    const props = node.props ?? {};
    const when = props.when;
    return typeof when === 'function' ? when() : when;
}
function trackForNode(node) {
    const props = node.props ?? {};
    const each = props.each;
    if (typeof each === 'function') {
        each();
    }
}
function getForSelection(node) {
    const props = node.props ?? {};
    const each = props.each;
    const resolved = typeof each === 'function'
        ? each()
        : each;
    return Array.isArray(resolved) && resolved.length > 0 ? resolved : 'fallback';
}
function getSwitchBranch(node, ensureNodeId) {
    const children = node.children ?? [];
    for (let index = 0; index < children.length; index += 1) {
        const child = children[index];
        const matchNode = ensureNodeId(child);
        if (matchNode.type !== 'Match') {
            continue;
        }
        const when = matchNode.props?.when;
        const value = typeof when === 'function' ? when() : when;
        if (value) {
            return index;
        }
    }
    return 'fallback';
}
export function isControlFlowNode(node) {
    return node.type === 'Show' || node.type === 'For' || node.type === 'SwitchFlow';
}
function resolveShowChildrenCached(node, ensureNodeId, cache) {
    const props = node.props ?? {};
    const when = props.when;
    const fallback = props.fallback;
    const children = props.children;
    const evaluate = typeof when === 'function' ? when : () => when;
    const value = evaluate();
    const branch = value ? 'primary' : 'fallback';
    const branchChildren = branch === 'primary' ? children : fallback;
    const childrenAreStatic = typeof branchChildren !== 'function';
    if (cache) {
        const cached = cache.get(node.id);
        if (childrenAreStatic && cached && cached.branch === branch) {
            return cached.children;
        }
    }
    const resolved = !value
        ? normalizeOptionalChildren(fallback, ensureNodeId)
        : typeof children === 'function'
            ? normalizeOptionalChildren(children(value), ensureNodeId)
            : normalizeOptionalChildren(children, ensureNodeId);
    if (childrenAreStatic) {
        cache?.set(node.id, { branch, children: resolved });
    }
    else {
        cache?.delete(node.id);
    }
    return resolved;
}
function resolveForChildren(node, keyValidation, ensureNodeId) {
    const props = node.props ?? {};
    const each = props.each;
    const fallback = props.fallback;
    const children = props.children;
    const keyFn = props.key;
    const resolved = typeof each === 'function'
        ? each()
        : each;
    const items = Array.isArray(resolved) ? resolved : [];
    if (items.length === 0) {
        return normalizeOptionalChildren(fallback, ensureNodeId);
    }
    const result = [];
    const rawKeyCounts = new Map();
    for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item === undefined || item === null) {
            continue;
        }
        const index = i;
        const output = children(item, () => index);
        if (Array.isArray(output)) {
            const keyBase = keyFn
                ? resolveDuplicateAwareKey(node, keyFn(item, () => index), rawKeyCounts, keyValidation)
                : undefined;
            output.forEach((child, childIndex) => {
                const normalized = ensureNodeId(child);
                if (!normalized.key && keyBase !== undefined) {
                    normalized.key = `${keyBase}:${childIndex}`;
                }
                result.push(normalized);
            });
            continue;
        }
        const normalized = ensureNodeId(output);
        if (!normalized.key && keyFn) {
            normalized.key = resolveDuplicateAwareKey(node, keyFn(item, () => index), rawKeyCounts, keyValidation);
        }
        result.push(normalized);
    }
    warnOnMissingForKeys(node, result, keyValidation);
    return result;
}
function resolveSwitchChildrenCached(node, ensureNodeId, cache) {
    const props = node.props ?? {};
    const fallback = props.fallback;
    const children = node.children ?? [];
    for (let index = 0; index < children.length; index += 1) {
        const child = children[index];
        const matchNode = ensureNodeId(child);
        if (matchNode.type !== 'Match') {
            continue;
        }
        const matchProps = matchNode.props ?? {};
        const when = matchProps.when;
        const evaluate = typeof when === 'function' ? when : () => when;
        const value = evaluate();
        if (!value) {
            continue;
        }
        const matchChildren = matchProps.children;
        const childrenAreStatic = typeof matchChildren !== 'function';
        if (cache) {
            const cached = cache.get(node.id);
            if (childrenAreStatic && cached && cached.branch === index) {
                return cached.children;
            }
        }
        const resolved = typeof matchChildren === 'function'
            ? normalizeOptionalChildren(matchChildren(value), ensureNodeId)
            : normalizeOptionalChildren(matchChildren, ensureNodeId);
        if (childrenAreStatic) {
            cache?.set(node.id, { branch: index, children: resolved });
        }
        else {
            cache?.delete(node.id);
        }
        return resolved;
    }
    if (cache) {
        const cached = cache.get(node.id);
        if (cached && cached.branch === 'fallback') {
            return cached.children;
        }
    }
    const resolvedFallback = normalizeOptionalChildren(fallback, ensureNodeId);
    cache?.set(node.id, { branch: 'fallback', children: resolvedFallback });
    return resolvedFallback;
}
function normalizeOptionalChildren(children, ensureNodeId) {
    if (!children) {
        return [];
    }
    return (Array.isArray(children) ? children : [children]).map((child) => ensureNodeId(child));
}
function warnOnMissingForKeys(node, children, keyValidation) {
    if (!keyValidation.warnOnMissingKeys
        || keyValidation.warnedForNodes.has(node.id)
        || children.length <= 1) {
        return;
    }
    const missingKey = children.some((child) => !child.key);
    if (!missingKey || !globalThis.console?.warn) {
        return;
    }
    globalThis.console.warn('[smoothie/ui] <For> items should include a stable `key` for optimal list performance.');
    keyValidation.warnedForNodes.add(node.id);
}
function resolveDuplicateAwareKey(node, rawKey, rawKeyCounts, keyValidation) {
    const duplicateIndex = rawKeyCounts.get(rawKey) ?? 0;
    rawKeyCounts.set(rawKey, duplicateIndex + 1);
    if (duplicateIndex === 0) {
        return rawKey;
    }
    if (keyValidation.warnOnMissingKeys
        && !keyValidation.warnedForDuplicateKeys.has(node.id)
        && globalThis.console?.error) {
        globalThis.console.error(`[smoothie/ui] <For> received duplicate key \`${rawKey}\`; using occurrence-qualified internal keys to avoid unstable reuse.`);
        keyValidation.warnedForDuplicateKeys.add(node.id);
    }
    return `${rawKey}::duplicate:${duplicateIndex}`;
}
function createItemSnapshot(value, depth = 2) {
    if (value === null
        || value === undefined
        || typeof value === 'string'
        || typeof value === 'number'
        || typeof value === 'boolean'
        || typeof value === 'bigint'
        || typeof value === 'symbol') {
        return value;
    }
    if (depth <= 0 || typeof value === 'function') {
        return { kind: 'opaque', value };
    }
    if (Array.isArray(value)) {
        return value.map((entry) => createItemSnapshot(entry, depth - 1));
    }
    if (typeof value === 'object') {
        const entries = Object.keys(value)
            .sort()
            .map((key) => [key, createItemSnapshot(value[key], depth - 1)]);
        return {
            kind: 'object',
            entries,
        };
    }
    return { kind: 'opaque', value };
}
function snapshotsEqual(left, right) {
    if (Object.is(left, right)) {
        return true;
    }
    if (Array.isArray(left) && Array.isArray(right)) {
        if (left.length !== right.length) {
            return false;
        }
        for (let index = 0; index < left.length; index += 1) {
            if (!snapshotsEqual(left[index], right[index])) {
                return false;
            }
        }
        return true;
    }
    if (typeof left === 'object' && left !== null
        && typeof right === 'object' && right !== null
        && 'kind' in left
        && 'kind' in right) {
        if (left.kind !== right.kind) {
            return false;
        }
        if (left.kind === 'opaque' && right.kind === 'opaque') {
            return Object.is(left.value, right.value);
        }
        if (left.kind === 'object' && right.kind === 'object') {
            if (left.entries.length !== right.entries.length) {
                return false;
            }
            for (let index = 0; index < left.entries.length; index += 1) {
                const [leftKey, leftValue] = left.entries[index];
                const [rightKey, rightValue] = right.entries[index];
                if (leftKey !== rightKey || !snapshotsEqual(leftValue, rightValue)) {
                    return false;
                }
            }
            return true;
        }
    }
    return false;
}
//# sourceMappingURL=control-flow.js.map